import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ArrowRight, Loader2, Check, Users, TrendingUp, Sparkles } from 'lucide-react';
import { onboardingQuestions, type Question } from '@/lib/onboardingQuestions';
import { computeArchetype, type OnboardingAnswers } from '@/lib/archetype';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { DinoLogo } from '@/components/DinoLogo';
import { useReadingGoals } from '@/hooks/useReadingGoals';

export default function OnboardingArchetype() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { createOrUpdateGoal } = useReadingGoals();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Partial<OnboardingAnswers>>({});
  const [customGoal, setCustomGoal] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

// Reset quiz state on mount and when route key changes (handles retakes)
  React.useEffect(() => {
    // Always reset on mount. Also catches same-path navigations with different state/query
    setCurrentStep(0);
    setAnswers({});
    setCustomGoal('');
  }, [location.key]);

  const currentQuestion = onboardingQuestions[currentStep];
  const progress = ((currentStep + 1) / onboardingQuestions.length) * 100;

  const handleAnswer = (questionId: string, value: string | number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleMultiSelectAnswer = (questionId: string, value: string) => {
    setAnswers((prev) => {
      const current = prev[questionId];
      const currentArray = Array.isArray(current) ? current : [];
      
      if (currentArray.includes(value)) {
        // Remove if already selected
        const newArray = currentArray.filter(v => v !== value);
        return { ...prev, [questionId]: newArray.length > 0 ? newArray : undefined };
      } else {
        // Add to selection
        return { ...prev, [questionId]: [...currentArray, value] };
      }
    });
  };

  const handleNext = () => {
    if (currentStep < onboardingQuestions.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleFinish = async () => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      // Ensure all required answers are present
      const growthAnswer = Array.isArray(answers.growth) && answers.growth.length > 0 
        ? answers.growth.join(', ') 
        : typeof answers.growth === 'string' 
          ? answers.growth 
          : 'Read more books overall';
      
      const completeAnswers: OnboardingAnswers = {
        mood: answers.mood || 'Surprise me',
        growth: growthAnswer,
        goal: answers.goal || 12,
        weeklyGoal: Number(answers.weeklyGoal) || 5
      };

      // Compute archetype
      const { archetype, scores } = computeArchetype(completeAnswers);

      // Create summary JSON
      const onboarding_summary = {
        archetype,
        scores,
        answers: completeAnswers,
        completedAt: new Date().toISOString()
      };

      // Save to database
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          archetype,
          reading_goal: completeAnswers.goal,
          reading_motivation: completeAnswers.growth,
          onboarding_summary
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Save weekly reading goal to user_reading_preferences
      const { error: prefsError } = await supabase
        .from('user_reading_preferences')
        .upsert(
          {
            user_id: user.id,
            weekly_reading_goal: completeAnswers.weeklyGoal,
            updated_at: new Date().toISOString()
          },
          { onConflict: 'user_id' }
        );

      if (prefsError) throw prefsError;

      // Also create/update reading goal in reading_goals table for current year
      const currentYear = new Date().getFullYear();
      await createOrUpdateGoal(currentYear, completeAnswers.goal);

      toast({
        title: 'Profile complete!',
        description: `Welcome, ${archetype}! Let's start your reading journey.`
      });

      // Navigate to results page
      navigate('/onboarding/results', { replace: true });
    } catch (error) {
      console.error('Error saving onboarding:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save your preferences. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAnswered = currentQuestion.multiSelect
    ? Array.isArray(answers[currentQuestion.id]) && answers[currentQuestion.id].length > 0
    : answers[currentQuestion.id] !== undefined;
  const canProceed = isAnswered;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur">
        <div className="container mx-auto px-4 py-4">
          <DinoLogo size={32} />
        </div>
      </header>

      {/* Progress Bar */}
      <div className="border-b bg-card/30">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground font-medium">
              Step {currentStep + 1} of {onboardingQuestions.length}
            </span>
            <Progress value={progress} className="flex-1" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">Discover Your Reader Archetype</h2>
          <p className="text-muted-foreground mb-4">
            Answer a few questions to help us understand your reading style
          </p>
          <Card className="p-4 mb-6">
            <p className="text-sm text-center mb-4">This helps us tailor your experience to unlock:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary flex-shrink-0" />
                <p className="text-xs">Shared adventures</p>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-secondary flex-shrink-0" />
                <p className="text-xs">Reading growth</p>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent flex-shrink-0" />
                <p className="text-xs">Perfect recommendations</p>
              </div>
            </div>
          </Card>
        </div>
        <Card className="shadow-soft">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl font-heading">{currentQuestion.title}</CardTitle>
            {currentQuestion.subtitle && (
              <CardDescription className="text-base">{currentQuestion.subtitle}</CardDescription>
            )}
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Choice Questions */}
            {currentQuestion.type === 'choice' && currentQuestion.options && (
              <div className="grid gap-3">
                {currentQuestion.multiSelect && (
                  <p className="text-sm text-muted-foreground text-center mb-2">
                    Select all that apply
                  </p>
                )}
                {currentQuestion.options.map((option) => {
                  const isSelected = currentQuestion.multiSelect
                    ? Array.isArray(answers[currentQuestion.id]) && answers[currentQuestion.id].includes(option.value)
                    : answers[currentQuestion.id] === option.value;
                  
                  return (
                    <Button
                      key={option.value}
                      variant={isSelected ? 'default' : 'outline'}
                      className="h-auto py-4 px-6 justify-start text-left"
                      onClick={() => 
                        currentQuestion.multiSelect 
                          ? handleMultiSelectAnswer(currentQuestion.id, option.value)
                          : handleAnswer(currentQuestion.id, option.value)
                      }
                    >
                      {option.icon && <span className="text-2xl mr-3">{option.icon}</span>}
                      <span className="font-medium">{option.label}</span>
                      {isSelected && (
                        <Check className="ml-auto h-5 w-5" />
                      )}
                    </Button>
                  );
                })}
              </div>
            )}

            {/* Number Question (Goal) */}
            {currentQuestion.type === 'number' && (
              <div className="space-y-4">
                <div className="grid grid-cols-5 gap-3">
                  {currentQuestion.presets?.map((preset) => (
                    <Button
                      key={preset}
                      variant={answers.goal === preset ? 'default' : 'outline'}
                      className="h-16 text-lg font-bold"
                      onClick={() => {
                        handleAnswer('goal', preset);
                        setCustomGoal('');
                      }}
                    >
                      {preset}
                    </Button>
                  ))}
                </div>

                <div className="relative">
                  <div className="text-center text-sm text-muted-foreground mb-2">
                    Or set your own goal
                  </div>
                  <Input
                    type="number"
                    min={currentQuestion.min}
                    max={currentQuestion.max}
                    placeholder="Enter custom goal"
                    value={customGoal}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setCustomGoal(e.target.value);
                      if (val >= (currentQuestion.min || 1) && val <= (currentQuestion.max || 200)) {
                        handleAnswer('goal', val);
                      }
                    }}
                    className="text-center text-lg"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={!canProceed || isSubmitting}
            className="gap-2"
          >
            {currentStep === onboardingQuestions.length - 1 ? (
              <>
                {isSubmitting ? 'Finishing...' : 'Finish'}
                <Check className="h-4 w-4" />
              </>
            ) : (
              <>
                Next
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
